/**
 * CommandPalette - PHASE 3: Universal Command Interface
 * 
 * FOUNDER DIRECTIVE: Command Bar becomes the primary navigation interface.
 * 
 * A minimal, bank-grade command palette (Notion-like calm).
 * Accessible via Cmd/Ctrl+K.
 * 
 * Features:
 * - Search input autofocus
 * - Arrow navigation, Enter to execute
 * - Esc closes
 * - Role-aware command visibility
 * - PHASE 3: Search commitments via GCI
 * - PHASE 3: Search organizations
 * - PHASE 3: Navigate to Observatory, Mission Control, Atlas
 * - PHASE 3: Launch simulation routes
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys, getMetaKeyLabel } from '../../contexts/HotkeyContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  Home,
  BookOpen,
  Eye,
  Shield,
  Building2,
  FileText,
  Users,
  Wallet,
  Settings,
  Search,
  ArrowRight,
  Sun,
  Moon,
  HelpCircle,
  Keyboard,
  BarChart3,
  Command,
  ChevronRight,
  Compass,
  Play,
  Scale,
  CreditCard,
  History,
  MessageSquare,
  // PHASE 3: Additional icons for Command Bar
  Globe,
  Radio,
  Activity,
  Target,
  Layers,
  MapPin,
  Zap,
  Network,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// PHASE 3: Command definitions - FOUNDER DIRECTIVE: Universal Command Interface
// =============================================================================
const ALL_COMMANDS = [
  // PHASE 3: PRIMARY NAVIGATION SURFACES
  {
    id: 'goto-home',
    label: 'Go to Home',
    shortcut: null,
    icon: Home,
    action: 'navigate',
    path: '/',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['home', 'main', 'start', 'homepage', 'landing'],
  },
  {
    id: 'goto-observatory',
    label: 'Go to Observatory',
    shortcut: null,
    icon: Globe,
    action: 'navigate',
    path: '/observatory',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['observatory', 'network', 'visualization', 'view', 'system', 'status', 'atlas', 'map'],
  },
  {
    id: 'goto-mission-control',
    label: 'Go to Mission Control',
    shortcut: null,
    icon: Radio,
    action: 'navigate',
    path: '/mission-control',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['mission', 'control', 'operations', 'governance', 'registry', 'csvr', 'commitments'],
  },
  {
    id: 'goto-granullo-atlas',
    label: 'Open Granullo Atlas',
    shortcut: null,
    icon: Network,
    action: 'navigate',
    path: '/granullo-atlas',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['atlas', 'network', 'graph', 'visualization', 'nodes', 'dependencies'],
  },
  {
    id: 'goto-simulation',
    label: 'Launch Simulation Environment',
    shortcut: null,
    icon: Zap,
    action: 'navigate',
    path: '/simulation',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['simulation', 'scenario', 'stress', 'test', 'model', 'cascade', 'network'],
  },
  {
    id: 'goto-control-room',
    label: 'Go to Control Room',
    shortcut: null,
    icon: Activity,
    action: 'navigate',
    path: '/control-room',
    category: 'Primary Surfaces',
    roles: null, // All roles
    keywords: ['control', 'room', 'impact', 'intelligence', 'telemetry'],
  },
  // Legacy Navigation
  {
    id: 'goto-dashboard',
    label: 'Go to Dashboard',
    shortcut: null,
    icon: Layers,
    action: 'navigate',
    path: '/dashboard',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops', 'donor'],
    keywords: ['dashboard', 'main', 'overview'],
  },
  {
    id: 'goto-glossary',
    label: 'Go to Glossary',
    shortcut: null,
    icon: BookOpen,
    action: 'navigate',
    path: '/glossary',
    category: 'Navigation',
    roles: null, // All roles
    keywords: ['terms', 'definitions', 'vocabulary', 'help'],
  },
  {
    id: 'goto-impact-intelligence',
    label: 'Go to Impact Intelligence',
    shortcut: null,
    icon: Eye,
    action: 'navigate',
    path: '/impact-intelligence',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops'],
    keywords: ['impact', 'analytics', 'data', 'intelligence'],
  },
  {
    id: 'goto-csr-hub',
    label: 'Go to CSR Hub',
    shortcut: null,
    icon: Shield,
    action: 'navigate',
    path: '/csr/start',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops', 'donor'],
    keywords: ['csr', 'corporate', 'social', 'responsibility'],
  },
  {
    id: 'goto-programs',
    label: 'Go to CSR Programs',
    shortcut: null,
    icon: Building2,
    action: 'navigate',
    path: '/csr/programs',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops'],
    keywords: ['programs', 'funds', 'disbursement'],
  },
  {
    id: 'goto-partners',
    label: 'Go to CSR Partners',
    shortcut: null,
    icon: Users,
    action: 'navigate',
    path: '/csr/partners',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops'],
    keywords: ['partners', 'organizations', 'ngo', 'foundations'],
  },
  {
    id: 'goto-allocations',
    label: 'Go to Allocations',
    shortcut: null,
    icon: Wallet,
    action: 'navigate',
    path: '/csr/allocations',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops'],
    keywords: ['allocations', 'funds', 'tracking'],
  },
  {
    id: 'goto-audit-narrative',
    label: 'Go to Audit Narrative',
    shortcut: null,
    icon: FileText,
    action: 'navigate',
    path: '/audit-narrative',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'auditor', 'regulator'],
    keywords: ['audit', 'narrative', 'compliance', 'evidence'],
  },
  {
    id: 'goto-governance-history',
    label: 'Go to Governance History',
    shortcut: null,
    icon: History,
    action: 'navigate',
    path: '/governance-history',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops', 'auditor', 'regulator'],
    keywords: ['governance', 'history', 'events', 'eligibility', 'stage', 'transitions'],
  },
  {
    id: 'goto-regulatory-walkthrough',
    label: 'Go to Regulatory Walkthrough',
    shortcut: null,
    icon: Scale,
    action: 'navigate',
    path: '/regulatory-walkthrough',
    category: 'Navigation',
    roles: ['super_admin', 'auditor', 'regulator', 'bank_partner'],
    keywords: ['regulatory', 'oversight', 'walkthrough', 'governance'],
  },
  {
    id: 'goto-card-applications',
    label: 'Go to Card Applications',
    shortcut: null,
    icon: CreditCard,
    action: 'navigate',
    path: '/bank/card-applications',
    category: 'Navigation',
    roles: ['super_admin', 'bank_partner'],
    keywords: ['card', 'applications', 'corporate', 'credit', 'governed'],
  },
  {
    id: 'goto-escrow-console',
    label: 'Go to Escrow Console',
    shortcut: null,
    icon: Building2,
    action: 'navigate',
    path: '/bank/escrow',
    category: 'Navigation',
    roles: ['super_admin', 'bank_partner', 'regulator', 'auditor'],
    keywords: ['escrow', 'bank', 'console', 'funds'],
  },
  {
    id: 'goto-execution-room',
    label: 'Go to Execution Room',
    shortcut: null,
    icon: Compass,
    action: 'navigate',
    path: '/execution-room',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops'],
    keywords: ['execution', 'room', 'coordination', 'program', 'stages', 'workflow'],
  },
  {
    id: 'goto-program-readiness',
    label: 'Go to Program Readiness',
    shortcut: null,
    icon: Shield,
    action: 'navigate',
    path: '/programs',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops'],
    keywords: ['readiness', 'go-live', 'status', 'domains', 'coordination', 'blockers'],
  },
  {
    id: 'search-domains',
    label: 'Search Domains',
    shortcut: null,
    icon: Search,
    action: 'search',
    searchType: 'domains',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops'],
    keywords: ['domains', 'search', 'coordination', 'find'],
  },
  {
    id: 'view-card-application',
    label: 'View Card Application',
    shortcut: null,
    icon: CreditCard,
    action: 'navigate',
    path: '/bank/card-applications',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops'],
    keywords: ['card', 'application', 'corporate', 'governed', 'issuance'],
  },
  {
    id: 'view-escrow-setup',
    label: 'View Escrow Setup',
    shortcut: null,
    icon: Shield,
    action: 'navigate',
    path: '/bank/escrow',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'bank_partner', 'bank_partner_admin', 'finance_ops', 'regulator', 'auditor'],
    keywords: ['escrow', 'setup', 'funding', 'coordination', 'artifacts'],
  },
  {
    id: 'goto-bank-onboarding',
    label: 'Go to Bank Onboarding',
    shortcut: null,
    icon: Building2,
    action: 'navigate',
    path: '/bank/onboarding',
    category: 'Navigation',
    roles: ['super_admin', 'bank_partner', 'bank_partner_admin'],
    keywords: ['bank', 'onboarding', 'setup', 'capabilities', 'mcc', 'mid'],
  },
  {
    id: 'goto-communication-doctrine',
    label: 'Communication Doctrine',
    shortcut: null,
    icon: HelpCircle,
    action: 'navigate',
    path: '/help/communication',
    category: 'Help',
    roles: null, // All roles
    keywords: ['communication', 'doctrine', 'messages', 'requests', 'domains', 'how', 'guide'],
  },
  {
    id: 'goto-why-granullo',
    label: 'Why Granullo?',
    shortcut: null,
    icon: HelpCircle,
    action: 'navigate',
    path: '/why-granullo',
    category: 'Help',
    roles: null, // All roles
    keywords: ['why', 'granullo', 'email', 'whatsapp', 'explain', 'understand'],
  },
  {
    id: 'open-auditor-view',
    label: 'Open Auditor View (Read-only)',
    shortcut: null,
    icon: Eye,
    action: 'navigate',
    path: '/programs/:id/auditor-view',
    category: 'Navigation',
    roles: ['super_admin', 'auditor', 'regulator'],
    keywords: ['auditor', 'regulator', 'read only', 'compliance', 'evidence'],
    requiresProgramContext: true,
  },
  {
    id: 'program-conversations',
    label: 'Talk about this program',
    shortcut: null,
    icon: MessageSquare,
    action: 'navigate',
    path: '/programs/:id/conversations',
    category: 'Communication',
    roles: null, // All roles
    keywords: ['talk', 'conversation', 'discuss', 'message', 'program'],
    requiresProgramContext: true,
  },
  {
    id: 'upload-mcc-catalog',
    label: 'Upload MCC Catalog',
    shortcut: null,
    icon: FileText,
    action: 'navigate',
    path: '/bank/onboarding',
    category: 'Actions',
    roles: ['super_admin', 'bank_partner', 'bank_partner_admin'],
    keywords: ['mcc', 'upload', 'merchant', 'category', 'catalog'],
  },
  {
    id: 'goto-analytics',
    label: 'Go to Analytics',
    shortcut: null,
    icon: BarChart3,
    action: 'navigate',
    path: '/csr/analytics',
    category: 'Navigation',
    roles: ['super_admin', 'client_admin', 'finance_ops'],
    keywords: ['analytics', 'reports', 'metrics', 'data'],
  },
  
  // Guided Tours
  {
    id: 'start-tour-csr',
    label: 'Start Guided Tour: CSR Overview',
    shortcut: null,
    icon: Play,
    action: 'tour',
    tourId: 'csr-overview',
    category: 'Guided Tours',
    roles: ['super_admin', 'client_admin', 'finance_ops', 'donor'],
    keywords: ['tour', 'guide', 'walkthrough', 'help', 'learn'],
  },
  {
    id: 'start-tour-governance',
    label: 'Start Guided Tour: Governance Lifecycle',
    shortcut: null,
    icon: Play,
    action: 'tour',
    tourId: 'governance-lifecycle',
    category: 'Guided Tours',
    roles: null, // All roles
    keywords: ['tour', 'guide', 'lifecycle', 'governance'],
  },
  
  // Actions
  {
    id: 'toggle-theme',
    label: 'Toggle Theme',
    shortcut: null,
    icon: Sun,
    action: 'theme',
    category: 'Actions',
    roles: null, // All roles
    keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
  },
  {
    id: 'show-hotkeys',
    label: 'Show Keyboard Shortcuts',
    shortcut: null,
    icon: Keyboard,
    action: 'hotkeys',
    category: 'Actions',
    roles: null, // All roles
    keywords: ['keyboard', 'shortcuts', 'hotkeys', 'help'],
  },
  
  // Search
  {
    id: 'search-glossary',
    label: 'Search Glossary',
    shortcut: null,
    icon: Search,
    action: 'navigate',
    path: '/glossary',
    category: 'Search',
    roles: null, // All roles
    keywords: ['search', 'find', 'glossary', 'terms'],
  },
  // PHASE 3: Search commitments instruction
  {
    id: 'search-commitments-hint',
    label: 'Search Commitments (type GCI-... or commitment name)',
    shortcut: null,
    icon: Target,
    action: 'hint',
    category: 'Search',
    roles: null,
    keywords: ['commitment', 'gci', 'search', 'find', 'registry'],
  },
  // PHASE 3: Search organizations instruction
  {
    id: 'search-organizations-hint',
    label: 'Search Organizations (type org name)',
    shortcut: null,
    icon: Building2,
    action: 'hint',
    category: 'Search',
    roles: null,
    keywords: ['organization', 'org', 'company', 'ministry', 'department', 'foundation'],
  },
];

// =============================================================================
// PHASE 3: Dynamic Search Functions
// =============================================================================

/**
 * Fetch commitments from GCI API for search
 */
async function searchCommitments(query) {
  try {
    const response = await fetch(`${API_URL}/api/gci/search/query?limit=10`);
    if (!response.ok) return [];
    
    const data = await response.json();
    const commitments = data.commitments || [];
    
    // Filter by query if provided
    if (query) {
      const queryLower = query.toLowerCase();
      return commitments.filter(c => 
        c.gci_id?.toLowerCase().includes(queryLower) ||
        c.commitment_title?.toLowerCase().includes(queryLower) ||
        c.organization?.toLowerCase().includes(queryLower)
      );
    }
    
    return commitments;
  } catch (error) {
    console.error('Failed to search commitments:', error);
    return [];
  }
}

/**
 * Extract unique organizations from commitments
 */
function extractOrganizations(commitments) {
  const orgs = new Map();
  commitments.forEach(c => {
    if (c.organization && !orgs.has(c.organization)) {
      orgs.set(c.organization, {
        name: c.organization,
        commitmentCount: 1,
        totalValue: c.commitment_value || 0,
      });
    } else if (c.organization) {
      const existing = orgs.get(c.organization);
      existing.commitmentCount++;
      existing.totalValue += c.commitment_value || 0;
    }
  });
  return Array.from(orgs.values());
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { isCommandPaletteOpen, closeCommandPalette, markHotkeyHintSeen } = useHotkeys();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showHotkeysModal, setShowHotkeysModal] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  
  // PHASE 3: Dynamic search state
  const [commitments, setCommitments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // FOUNDER DIRECTIVE: Recent Commands (last 5)
  const [recentCommands, setRecentCommands] = useState(() => {
    try {
      const stored = localStorage.getItem('granullo_recent_commands');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  // Save recent command
  const addRecentCommand = useCallback((command) => {
    setRecentCommands(prev => {
      // Remove if already exists, add to front
      const filtered = prev.filter(c => c.id !== command.id);
      const updated = [{ id: command.id, label: command.label, path: command.path, timestamp: Date.now() }, ...filtered].slice(0, 5);
      try {
        localStorage.setItem('granullo_recent_commands', JSON.stringify(updated));
      } catch {}
      return updated;
    });
  }, []);
  
  const metaKey = getMetaKeyLabel();
  const userRole = user?.role || 'guest';
  
  // PHASE 3: Fetch commitments when palette opens
  useEffect(() => {
    if (isCommandPaletteOpen) {
      setIsLoading(true);
      searchCommitments('')
        .then(results => {
          setCommitments(results);
          setOrganizations(extractOrganizations(results));
        })
        .finally(() => setIsLoading(false));
    }
  }, [isCommandPaletteOpen]);
  
  // PHASE 3: Build dynamic commitment commands
  const commitmentCommands = useMemo(() => {
    if (!search || search.length < 2) return [];
    
    const searchLower = search.toLowerCase();
    return commitments
      .filter(c => 
        c.gci_id?.toLowerCase().includes(searchLower) ||
        c.commitment_title?.toLowerCase().includes(searchLower) ||
        c.organization?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5)
      .map(c => ({
        id: `commitment-${c.gci_id}`,
        label: c.commitment_title || c.gci_id,
        sublabel: `${c.organization} · $${(c.commitment_value || 0).toLocaleString()}`,
        icon: Target,
        action: 'navigate',
        path: `/gci/${c.gci_id}`,
        category: 'Commitments',
        gciId: c.gci_id,
      }));
  }, [commitments, search]);
  
  // PHASE 3: Build dynamic organization commands
  const organizationCommands = useMemo(() => {
    if (!search || search.length < 2) return [];
    
    const searchLower = search.toLowerCase();
    return organizations
      .filter(org => org.name.toLowerCase().includes(searchLower))
      .slice(0, 5)
      .map(org => ({
        id: `org-${org.name.replace(/\s+/g, '-')}`,
        label: org.name,
        sublabel: `${org.commitmentCount} commitment${org.commitmentCount > 1 ? 's' : ''} · $${org.totalValue.toLocaleString()}`,
        icon: Building2,
        action: 'navigate',
        path: `/mission-control?org=${encodeURIComponent(org.name)}`,
        category: 'Organizations',
      }));
  }, [organizations, search]);
  
  // Filter commands based on role and search
  const filteredCommands = useMemo(() => {
    return ALL_COMMANDS.filter((cmd) => {
      // Role check
      if (cmd.roles && !cmd.roles.includes(userRole)) {
        return false;
      }
      
      // Hide hint commands when actively searching
      if (cmd.action === 'hint' && search.length >= 2) {
        return false;
      }
      
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchLabel = cmd.label.toLowerCase().includes(searchLower);
        const matchKeywords = cmd.keywords?.some(k => k.includes(searchLower));
        const matchCategory = cmd.category.toLowerCase().includes(searchLower);
        return matchLabel || matchKeywords || matchCategory;
      }
      
      return true;
    });
  }, [search, userRole]);
  
  // PHASE 3: Combine all commands (static + dynamic)
  const allFilteredCommands = useMemo(() => {
    return [
      ...commitmentCommands,
      ...organizationCommands,
      ...filteredCommands,
    ];
  }, [commitmentCommands, organizationCommands, filteredCommands]);
  
  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = {};
    
    // FOUNDER DIRECTIVE: Add Recent Commands section (only when not searching)
    if (!search && recentCommands.length > 0) {
      groups['Recent Commands'] = recentCommands.map(rc => {
        const originalCmd = ALL_COMMANDS.find(c => c.id === rc.id);
        return {
          id: `recent-${rc.id}`,
          label: rc.label,
          icon: History,
          action: 'navigate',
          path: rc.path,
          category: 'Recent Commands',
          roles: null,
          keywords: [],
          isRecent: true,
          originalCommand: originalCmd
        };
      });
    }
    
    // PHASE 3: Use allFilteredCommands to include dynamic results
    allFilteredCommands.forEach((cmd) => {
      if (!groups[cmd.category]) {
        groups[cmd.category] = [];
      }
      groups[cmd.category].push(cmd);
    });
    return groups;
  }, [allFilteredCommands, search, recentCommands]);
  
  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);
  
  // Focus input when palette opens
  useEffect(() => {
    if (isCommandPaletteOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      setSearch('');
      setSelectedIndex(0);
      markHotkeyHintSeen();
    }
  }, [isCommandPaletteOpen, markHotkeyHintSeen]);
  
  // Execute command
  const executeCommand = useCallback((cmd) => {
    // FOUNDER DIRECTIVE: Track recent commands
    if (cmd.action === 'navigate' && cmd.path) {
      addRecentCommand(cmd);
    }
    
    closeCommandPalette();
    
    switch (cmd.action) {
      case 'navigate':
        navigate(cmd.path);
        break;
      case 'theme':
        setTheme(theme === 'dark' ? 'light' : 'dark');
        break;
      case 'hotkeys':
        setShowHotkeysModal(true);
        break;
      case 'tour':
        // Navigate to tour start (handled by tour system)
        navigate(`/csr/hub?tour=${cmd.tourId}`);
        break;
      case 'hint':
        // Hint commands are informational only - no action
        break;
      // PHASE 3: Atlas node action
      case 'atlas-node':
        navigate(`/granullo-atlas?node=${cmd.nodeId}`);
        break;
      default:
        console.warn('Unknown command action:', cmd.action);
    }
  }, [closeCommandPalette, navigate, setTheme, theme, addRecentCommand]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < allFilteredCommands.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev > 0 ? prev - 1 : allFilteredCommands.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (allFilteredCommands[selectedIndex]) {
          executeCommand(allFilteredCommands[selectedIndex]);
        }
        break;
      // ESC handled by Dialog's onOpenChange and HotkeyContext global handler
    }
  }, [allFilteredCommands, selectedIndex, executeCommand]);
  
  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);
  
  return (
    <>
      <Dialog open={isCommandPaletteOpen} onOpenChange={closeCommandPalette}>
        <DialogContent 
          className="max-w-lg p-0 gap-0 overflow-hidden"
          onKeyDown={handleKeyDown}
          aria-describedby="command-palette-description"
          data-testid="command-palette-dialog"
        >
          {/* Accessible title - visually hidden but announced by screen readers */}
          <DialogTitle className="sr-only">Command Palette</DialogTitle>
          <DialogDescription id="command-palette-description" className="sr-only">
            Search and navigate Granullo. Use arrow keys to navigate, Enter to select, Escape to close.
          </DialogDescription>
          
          {/* Search Input */}
          <div className="flex items-center gap-2 px-3 border-b border-border">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search commands, commitments, organizations..."
              className="border-0 focus-visible:ring-0 px-0 h-12"
              data-testid="command-palette-search"
            />
            {isLoading && (
              <div className="h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            <Badge variant="secondary" className="text-[10px] shrink-0">
              {metaKey}+K
            </Badge>
          </div>
          
          {/* Command List */}
          <div 
            ref={listRef}
            className="max-h-[350px] overflow-y-auto py-2"
            role="listbox"
            aria-label="Commands"
          >
            {allFilteredCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No commands found
              </div>
            ) : (
              Object.entries(groupedCommands).map(([category, commands]) => (
                <div key={category} data-testid={`command-category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    {category}
                    {(category === 'Commitments' || category === 'Organizations') && (
                      <Badge variant="outline" className="text-[8px] px-1 py-0">LIVE</Badge>
                    )}
                  </div>
                  {commands.map((cmd) => {
                    const globalIndex = allFilteredCommands.indexOf(cmd);
                    const isSelected = globalIndex === selectedIndex;
                    const Icon = cmd.icon;
                    
                    // PHASE 3: Skip hint commands in render if action is hint
                    if (cmd.action === 'hint') {
                      return (
                        <div
                          key={cmd.id}
                          className="flex items-center gap-3 px-3 py-2 text-muted-foreground/60 italic"
                          data-testid={`command-${cmd.id}`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 text-xs">{cmd.label}</span>
                        </div>
                      );
                    }
                    
                    return (
                      <div
                        key={cmd.id}
                        data-index={globalIndex}
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors',
                          isSelected 
                            ? 'bg-muted text-foreground' 
                            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                        )}
                        onClick={() => executeCommand(cmd)}
                        data-testid={`command-${cmd.id}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {/* PHASE 3: Support sublabel for commitments/organizations */}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm block truncate">{cmd.label}</span>
                          {cmd.sublabel && (
                            <span className="text-xs text-muted-foreground block truncate">{cmd.sublabel}</span>
                          )}
                        </div>
                        {cmd.gciId && (
                          <Badge variant="outline" className="text-[9px] shrink-0">{cmd.gciId.split('-').slice(-1)[0]}</Badge>
                        )}
                        {isSelected && (
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>Esc Close</span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="h-3 w-3" />
              <span>Command Palette</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hotkeys Modal */}
      <Dialog open={showHotkeysModal} onOpenChange={setShowHotkeysModal}>
        <DialogContent className="max-w-sm">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Keyboard className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Keyboard Shortcuts</h2>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Open command palette</span>
                <Badge variant="outline" className="text-xs">{metaKey}+K</Badge>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Undo last action</span>
                <Badge variant="outline" className="text-xs">{metaKey}+Z</Badge>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Close / Exit</span>
                <Badge variant="outline" className="text-xs">Esc</Badge>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              These shortcuts work globally across Granullo, except when typing in input fields.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CommandPalette;
